import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalRequestDtoIn, PaymentDeviceSummaryDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { ApprovePaymentDeviceRequestDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import {
  MANAGEMENT_DEVICES_URL,
  MANAGEMENT_DEVICE_REQUESTS_URL,
  formatDeviceDate,
  paymentDeviceErrorMessage,
  unheldDeviceCount,
} from '@/helpers/paymentDevices';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import HeldRequestsList from '../HeldRequestsList';
import { DialogActions, ErrorLine, NoteField } from './dialogParts';

interface ApproveRequestDialogProps {
  request: PaymentDeviceRentalRequestDtoIn | null;
  onClose: () => void;
  /** No free device at all: the office goes straight to rejecting with that reason. */
  onRejectNoDevices: (request: PaymentDeviceRentalRequestDtoIn) => void;
  /** Opens one of the requests holding the free devices; without it they are listed as text. */
  onOpenHeldRequest?: (requestId: number) => void;
}

/**
 * Approving a request takes one device out of the free stock without naming it (HAT-1434, the office
 * 29/10): the device itself is picked when the fund comes for it. The hold lasts two pickup days, and
 * the server refuses the approval when every free device is already held for another request.
 */
const ApproveRequestDialog = ({ request, onClose, onRejectNoDevices, onOpenHeldRequest }: ApproveRequestDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();

  const { data: summary, isLoading } = useGetAuthApi<PaymentDeviceSummaryDtoIn>(request ? `${MANAGEMENT_DEVICES_URL}/summary` : '');

  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setNote('');
    setError(null);
  }, [request]);

  if (!request) return null;

  const unheld = summary ? unheldDeviceCount(summary) : null;
  const noneAvailable = !isLoading && unheld != null && unheld <= 0;
  const heldRequests = summary?.heldRequests ?? [];
  const free = unheld != null ? unheld + (summary?.heldCount ?? 0) : 0;

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<PaymentDeviceRentalRequestDtoIn, ApprovePaymentDeviceRequestDtoOut>(
        `${MANAGEMENT_DEVICE_REQUESTS_URL}/${request.id}/approve`,
        { note: note.trim() || null },
      );
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('paymentDevices.dialogs.approveTitle', { id: request.id })}
      maxWidth="560px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        <p className="body-sm-regular text-(--font-secondary)">
          {t('paymentDevices.dialogs.approveSummary', {
            fundId: request.fundId,
            start: formatDeviceDate(request.startDate),
            end: formatDeviceDate(request.expectedReturnDate),
            area: t(`paymentDevices.pickupArea.${request.pickupArea}`),
          })}
          {request.fundName ? ` · ${request.fundName}` : ''}
        </p>
        {request.notes && <p className="body-sm-regular whitespace-pre-line">{request.notes}</p>}

        {noneAvailable ? (
          // The list of devices may show free ones: say who holds them, and how to free one.
          <div className="flex flex-col gap-2">
            <p className="body-sm-semibold text-red-500">
              {free > 0 ? t('paymentDevices.dialogs.allFreeHeld', { count: free }) : t('paymentDevices.dialogs.noFreeDevice')}
            </p>
            {heldRequests.length > 0 && (
              <>
                <HeldRequestsList requests={heldRequests} onOpen={onOpenHeldRequest} />
                <p className="body-sm-regular text-(--font-secondary)">{t('paymentDevices.dialogs.freeHeldHint')}</p>
              </>
            )}
          </div>
        ) : (
          <>
            {unheld != null && (
              <p className="body-sm-semibold">{t('paymentDevices.dialogs.unheldDevices', { count: unheld })}</p>
            )}
            <p className="body-sm-regular text-(--font-secondary)">{t('paymentDevices.dialogs.approveHoldHint')}</p>
            <NoteField id="pd-approve-note" label={t('paymentDevices.dialogs.noteToCoordinator')} value={note} onChange={setNote} />
          </>
        )}
        <ErrorLine error={error} />
        <DialogActions
          onCancel={onClose}
          onConfirm={handleSubmit}
          isSubmitting={isSubmitting}
          confirmDisabled={noneAvailable || isLoading}
          confirmText={t('paymentDevices.actions.approve')}
          extra={noneAvailable ? (
            <Button variant="outline" className="!text-red-500 !border-red-300" onClick={() => onRejectNoDevices(request)}>
              {t('paymentDevices.dialogs.rejectNoDevices')}
            </Button>
          ) : undefined}
        />
      </div>
    </MainDialogLayout>
  );
};

export default ApproveRequestDialog;
